import type { RouteLocationNamedRaw, Router } from "vue-router";

import type { BoardType } from "@/model/baseTypes";

export const PARAM = {
  CURRENT_TEAM: "_",
  // the team the user last accessed
  LAST_TEAM: "^",
  // used when the app is initially loaded, without session/team/board params
  INITIAL: "-",
  // when there's no session at all
  NONE: "?",
  // the lastest session that is in planning or in execution
  LATEST: "latest",
};

export const ANY_TEAM = "*";

export type SyncRouter = Router & {
  syncPush: (f: () => RouteLocationNamedRaw) => ReturnType<Router["push"]>;
  syncReplace: (f: () => RouteLocationNamedRaw) => ReturnType<Router["replace"]>;
};

export type AppParams = Record<"session" | "team" | "board" | "name" | "query", string>;

export type LogoutReason =
  | "jwt-revoked"
  | "refresh-failed"
  | "connection-refused"
  | "account-expired"
  | "user-invoked"
  | "inactivity"
  | "no-session"
  | "cannot-subscribe"
  | "unknown";

export type BoardTarget =
  | BoardType
  | { type: "flex"; name?: string; session?: string }
  | { type: Exclude<BoardType, "flex">; team?: { name: string }; artId?: string; session?: string };

const queryProps = [
  "zoom",
  "scrollX",
  "scrollY",
  "feature",
  "category",
  "stickyId",
  "highlightStickyId",
  "searchText",
  "searchFlag",
  "searchAssignee",
  "searchReporter",
  "searchTeam",
  "searchArt",
  "searchIteration",
  "searchType",
  "searchLink",
  "searchSignal",
  "searchStatus",
  "searchDepLink",
  "searchUpperLevelLinks",
] as const;

export type QueryImpl = Partial<Record<(typeof queryProps)[number], string>>;

export function toQueryImpl(query: Iterable<[string, string]>): QueryImpl {
  return Object.fromEntries([...query].filter(([key, _]) => queryProps.includes(key)));
}

export interface Params {
  session?: string;
  team?: string;
  art?: string;
  board?: string;
  name?: string;
}

export interface Query {
  zoom?: string;
  scrollX?: string;
  scrollY?: string;
  search?: SearchQuery;
  category?: string;
  stickyId?: string;
  modal?: string;
}

export interface SearchQuery {
  text?: string;
  flag?: string;
  assignee?: string;
  reporter?: string;
  team?: string;
  art?: string;
  iteration?: string;
  type?: string;
  depLink?: string;
  link?: string;
  signal?: string;
  status?: string;
  upperLevelLinks?: string;
}
